import { GrossPrice, PricingBreakdown } from '@/components/billing/PricingBreakdown';
import { usePricingDisplayCatalog } from '@/hooks/usePricingDisplay';
import { supabase } from '@/integrations/supabase/client';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import {
  planIntroPricing,
  planPricingForCycle,
  validateCreditCartPricingQuote,
  type PricingDisplayQuote,
} from '@/lib/billing/pricingDisplay';
import { useTranslation } from 'react-i18next';

type Platform = 'forkhire' | 'forkseek';

function clientFor(platform: Platform) {
  return platform === 'forkseek' ? seekSupabase : supabase;
}

export function CreditPackGrossPrice({
  platform,
  packId,
}: {
  platform: Platform;
  packId: string;
}) {
  const { data } = usePricingDisplayCatalog(clientFor(platform), platform);
  const pricing = data?.credit_packs?.[packId];
  return pricing ? <GrossPrice pricing={pricing} /> : null;
}

export function PlanGrossPrice({
  platform,
  planId,
  billingCycle,
  introductory = false,
}: {
  platform: Platform;
  planId: string;
  billingCycle: 'monthly' | 'yearly';
  introductory?: boolean;
}) {
  const { data } = usePricingDisplayCatalog(clientFor(platform), platform);
  const pricing = introductory && billingCycle === 'monthly'
    ? planIntroPricing(data, planId) ?? planPricingForCycle(data, planId, billingCycle)
    : planPricingForCycle(data, planId, billingCycle);
  return pricing ? <GrossPrice pricing={pricing} /> : null;
}

export function PlanPricingBreakdown({
  platform,
  planId,
  billingCycle,
  introductory = false,
}: {
  platform: Platform;
  planId: string;
  billingCycle: 'monthly' | 'yearly';
  introductory?: boolean;
}) {
  const { data } = usePricingDisplayCatalog(clientFor(platform), platform);
  const pricing = introductory && billingCycle === 'monthly'
    ? planIntroPricing(data, planId) ?? planPricingForCycle(data, planId, billingCycle)
    : planPricingForCycle(data, planId, billingCycle);
  return pricing ? <PricingBreakdown pricing={pricing} /> : null;
}

export function CreditCartPricing({
  quote,
  breakdown = false,
}: {
  quote: PricingDisplayQuote | null;
  breakdown?: boolean;
}) {
  if (!quote?.display) return null;
  return breakdown
    ? <PricingBreakdown pricing={quote.display} />
    : <GrossPrice pricing={quote.display} size="small" />;
}

export function CreditCartLinePricing({
  quote,
  packId,
  value,
}: {
  quote: PricingDisplayQuote | null;
  packId: string;
  value: 'unit' | 'line';
}) {
  const line = quote?.lines?.find((candidate) => candidate.source_id === packId);
  const pricing = value === 'unit' ? line?.unit_display : line?.display;
  return pricing ? <GrossPrice pricing={pricing} size="small" /> : null;
}

export function CreditCartPricingStatus({
  ready,
  reason,
}: {
  ready: boolean;
  reason: ReturnType<typeof validateCreditCartPricingQuote>['reason'];
}) {
  const { t } = useTranslation();
  if (ready || reason === 'empty') return null;
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
      {reason === 'loading'
        ? t('pricingDisplay.loading')
        : t('pricingDisplay.unavailable')}
    </div>
  );
}
